'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus, FileText, Clock, Hash, ChevronRight, BookOpen, Lightbulb, Award, PenLine, Calendar, BarChart2, ArrowUpRight,  } from 'lucide-react';
import Badge from '@/components/ui/Badge';

// ─── Mock Data ────────────────────────────────────────────────────────────────

interface EssayHistoryEntry {
  id: string;
  date: string;
  shortDate: string;
  topic: string;
  type: string;
  wordCount: number;
  overallBand: number;
  TR: number;
  CC: number;
  LR: number;
  GRA: number;
}

const essayHistory: EssayHistoryEntry[] = [
  { id: 'e1', date: '12 Jan 2026', shortDate: 'Jan 12', topic: 'Technology & Society', type: 'Opinion', wordCount: 264, overallBand: 5.5, TR: 5.5, CC: 5.5, LR: 5.0, GRA: 5.5 },
  { id: 'e2', date: '19 Jan 2026', shortDate: 'Jan 19', topic: 'Education', type: 'Discussion', wordCount: 278, overallBand: 5.5, TR: 6.0, CC: 5.5, LR: 5.0, GRA: 6.0 },
  { id: 'e3', date: '28 Jan 2026', shortDate: 'Jan 28', topic: 'Environment', type: 'Problems & Solutions', wordCount: 291, overallBand: 6.0, TR: 6.0, CC: 6.0, LR: 5.5, GRA: 6.0 },
  { id: 'e4', date: '5 Feb 2026', shortDate: 'Feb 5', topic: 'Health', type: 'Advantages & Disadvantages', wordCount: 302, overallBand: 6.0, TR: 6.0, CC: 6.0, LR: 5.5, GRA: 6.5 },
  { id: 'e5', date: '14 Feb 2026', shortDate: 'Feb 14', topic: 'Crime & Justice', type: 'Opinion', wordCount: 287, overallBand: 6.0, TR: 6.0, CC: 6.0, LR: 5.5, GRA: 6.5 },
  { id: 'e6', date: '22 Feb 2026', shortDate: 'Feb 22', topic: 'Globalisation', type: 'Discussion', wordCount: 315, overallBand: 6.5, TR: 6.5, CC: 6.5, LR: 6.0, GRA: 6.5 },
  { id: 'e7', date: '3 Mar 2026', shortDate: 'Mar 3', topic: 'Government & Policy', type: 'Double Question', wordCount: 298, overallBand: 6.5, TR: 7.0, CC: 6.5, LR: 6.0, GRA: 7.0 },
];

const trendData = essayHistory.map((e) => ({
  date: e.shortDate,
  Overall: e.overallBand,
  TR: e.TR,
  CC: e.CC,
  LR: e.LR,
  GRA: e.GRA,
}));

const latestEntry = essayHistory[essayHistory.length - 1];
const previousEntry = essayHistory[essayHistory.length - 2];

const avgCriteria = {
  TR: parseFloat((essayHistory.reduce((s, e) => s + e.TR, 0) / essayHistory.length).toFixed(2)),
  CC: parseFloat((essayHistory.reduce((s, e) => s + e.CC, 0) / essayHistory.length).toFixed(2)),
  LR: parseFloat((essayHistory.reduce((s, e) => s + e.LR, 0) / essayHistory.length).toFixed(2)),
  GRA: parseFloat((essayHistory.reduce((s, e) => s + e.GRA, 0) / essayHistory.length).toFixed(2)),
};

const criterionBarData = [
  { name: 'Task Response', code: 'TR', avg: avgCriteria.TR, latest: latestEntry.TR },
  { name: 'Coherence & Cohesion', code: 'CC', avg: avgCriteria.CC, latest: latestEntry.CC },
  { name: 'Lexical Resource', code: 'LR', avg: avgCriteria.LR, latest: latestEntry.LR },
  { name: 'Grammatical Range', code: 'GRA', avg: avgCriteria.GRA, latest: latestEntry.GRA },
];

const radarData = [
  { criterion: 'TR', score: latestEntry.TR, fullMark: 9 },
  { criterion: 'CC', score: latestEntry.CC, fullMark: 9 },
  { criterion: 'LR', score: latestEntry.LR, fullMark: 9 },
  { criterion: 'GRA', score: latestEntry.GRA, fullMark: 9 },
];

interface RecommendedTopic {
  id: string;
  topic: string;
  reason: string;
  priority: 'High' | 'Medium' | 'Low';
  criterion: string;
  essayType: string;
}

const recommendedTopics: RecommendedTopic[] = [
  {
    id: 'r1',
    topic: 'Vocabulary in Environmental Issues',
    reason: 'Lexical Resource is your weakest criterion (avg 5.6). Practising topic-specific vocabulary for environment essays will directly improve LR scores.',
    priority: 'High',
    criterion: 'LR',
    essayType: 'Problems & Solutions',
  },
  {
    id: 'r2',
    topic: 'Academic Vocabulary for Technology Essays',
    reason: 'Repeated use of informal and imprecise vocabulary in technology-themed essays. Focus on collocations and topic-specific lexis.',
    priority: 'High',
    criterion: 'LR',
    essayType: 'Opinion',
  },
  {
    id: 'r3',
    topic: 'Cohesive Devices in Discussion Essays',
    reason: 'Coherence & Cohesion scores plateau at 6.5. Practising varied linking strategies in discussion essays will help break through.',
    priority: 'Medium',
    criterion: 'CC',
    essayType: 'Discussion',
  },
  {
    id: 'r4',
    topic: 'Developing Arguments in Double Question Essays',
    reason: 'Task Response scores show inconsistency in double-question formats. Practise fully addressing both parts with equal depth.',
    priority: 'Medium',
    criterion: 'TR',
    essayType: 'Double Question',
  },
  {
    id: 'r5',
    topic: 'Complex Sentence Structures in Health Essays',
    reason: 'GRA is your strongest criterion but still has room to grow. Practise embedding relative clauses and conditionals in health-themed writing.',
    priority: 'Low',
    criterion: 'GRA',
    essayType: 'Advantages & Disadvantages',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getBandColor(score: number): string {
  if (score >= 8.5) return '#16a34a';
  if (score >= 7.5) return '#2563eb';
  if (score >= 6.5) return '#0891b2';
  if (score >= 5.5) return '#d97706';
  if (score >= 4.5) return '#ea580c';
  return '#dc2626';
}

function getBandTextClass(score: number): string {
  if (score >= 8.5) return 'text-[color:var(--band-9)]';
  if (score >= 7.5) return 'text-[color:var(--band-8)]';
  if (score >= 6.5) return 'text-[color:var(--band-7)]';
  if (score >= 5.5) return 'text-[color:var(--band-6)]';
  if (score >= 4.5) return 'text-[color:var(--band-5)]';
  return 'text-danger';
}

function getBandBgClass(score: number): string {
  if (score >= 8.5) return 'score-card-9';
  if (score >= 7.5) return 'score-card-8';
  if (score >= 6.5) return 'score-card-7';
  if (score >= 5.5) return 'score-card-6';
  if (score >= 4.5) return 'score-card-5';
  return 'score-card-low';
}

function getBandLabel(score: number): string {
  if (score >= 9) return 'Expert';
  if (score >= 8) return 'Very Good';
  if (score >= 7) return 'Good';
  if (score >= 6) return 'Competent';
  if (score >= 5) return 'Modest';
  if (score >= 4) return 'Limited';
  return 'Extremely Limited';
}

function getTrend(current: number, previous: number) {
  const diff = parseFloat((current - previous).toFixed(1));
  if (diff > 0) return { icon: TrendingUp, label: `+${diff}`, color: 'text-[color:var(--success)]' };
  if (diff < 0) return { icon: TrendingDown, label: `${diff}`, color: 'text-danger' };
  return { icon: Minus, label: '—', color: 'text-muted-foreground' };
}

const priorityVariant: Record<string, 'danger' | 'warning' | 'secondary'> = {
  High: 'danger',
  Medium: 'warning',
  Low: 'secondary',
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function CustomLineTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border rounded-xl shadow-lg px-4 py-3 text-xs min-w-[140px]">
      <p className="font-semibold text-foreground mb-2">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center justify-between gap-4 mb-1">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground">{p.name}</span>
          </span>
          <span className="font-bold text-foreground">{p.value.toFixed(1)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type ViewMode = 'overall' | 'criteria';

export default function ProgressContent() {
  const [trendView, setTrendView] = useState<ViewMode>('overall');
  const overallTrend = getTrend(latestEntry.overallBand, previousEntry.overallBand);
  const TrendIcon = overallTrend.icon;

  const totalEssays = essayHistory.length;
  const avgOverall = parseFloat((essayHistory.reduce((s, e) => s + e.overallBand, 0) / totalEssays).toFixed(1));
  const bestBand = Math.max(...essayHistory.map((e) => e.overallBand));
  const avgWords = Math.round(essayHistory.reduce((s, e) => s + e.wordCount, 0) / totalEssays);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
            <span>Dashboard</span>
            <ChevronRight size={12} />
            <span className="text-foreground font-medium">Progress & Analytics</span>
          </div>
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Progress & Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track your band score trends, criterion performance, and personalised practice recommendations.
          </p>
        </div>
        <Link
          href="/"
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 shadow-sm transition-all duration-150 active:scale-95 shrink-0 self-start"
        >
          <PenLine size={14} />
          Write New Essay
        </Link>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Essays Submitted', value: totalEssays, unit: '', icon: FileText, color: 'bg-primary/10 text-primary' },
          { label: 'Average Band', value: avgOverall.toFixed(1), unit: '', icon: BarChart2, color: 'bg-[color:var(--info-bg)] text-[color:var(--info)]' },
          { label: 'Best Band Score', value: bestBand.toFixed(1), unit: '', icon: Award, color: 'bg-[color:var(--success-bg)] text-[color:var(--success)]' },
          { label: 'Avg Word Count', value: avgWords, unit: 'words', icon: Hash, color: 'bg-[color:var(--warning-bg)] text-[color:var(--warning)]' },
        ].map((stat) => {
          const StatIcon = stat.icon;
          return (
            <div key={stat.label} className="bg-card rounded-xl border p-4 shadow-sm flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${stat.color}`}>
                <StatIcon size={18} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{stat.label}</p>
                <p className="text-xl font-extrabold text-foreground band-score-display">
                  {stat.value}{stat.unit ? <span className="text-xs font-semibold text-muted-foreground ml-1">{stat.unit}</span> : null}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Grid: Trend Chart + Radar */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">

        {/* Band Score Trend Chart */}
        <div className="xl:col-span-2 bg-card rounded-2xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="text-sm font-semibold text-foreground">Band Score Trend</p>
              <p className="text-xs text-muted-foreground mt-0.5">Score progression across {totalEssays} essays</p>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => setTrendView('overall')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-150 ${trendView === 'overall' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Overall
              </button>
              <button
                onClick={() => setTrendView('criteria')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-150 ${trendView === 'criteria' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                By Criterion
              </button>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trendData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <YAxis domain={[4, 9]} ticks={[4, 5, 6, 7, 8, 9]} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomLineTooltip />} />
              {trendView === 'overall' ? (
                <Line
                  type="monotone"
                  dataKey="Overall"
                  stroke="var(--primary)"
                  strokeWidth={2.5}
                  dot={{ fill: 'var(--primary)', r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 6, strokeWidth: 0 }}
                />
              ) : (
                <>
                  <Line type="monotone" dataKey="TR" stroke="#1d4ed8" strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: '#1d4ed8' }} />
                  <Line type="monotone" dataKey="CC" stroke="#0891b2" strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: '#0891b2' }} />
                  <Line type="monotone" dataKey="LR" stroke="#d97706" strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: '#d97706' }} />
                  <Line type="monotone" dataKey="GRA" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, strokeWidth: 0, fill: '#16a34a' }} />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>

          {trendView === 'criteria' && (
            <div className="flex items-center gap-4 mt-3 flex-wrap">
              {[
                { label: 'TR', color: '#1d4ed8' },
                { label: 'CC', color: '#0891b2' },
                { label: 'LR', color: '#d97706' },
                { label: 'GRA', color: '#16a34a' },
              ].map((l) => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <span className="w-3 h-0.5 rounded-full inline-block" style={{ backgroundColor: l.color }} />
                  <span className="text-xs text-muted-foreground">{l.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Latest Criterion Radar */}
        <div className="bg-card rounded-2xl border shadow-sm p-5 flex flex-col">
          <div className="mb-3">
            <p className="text-sm font-semibold text-foreground">Latest Criterion Profile</p>
            <p className="text-xs text-muted-foreground mt-0.5">{latestEntry.date} — {latestEntry.topic}</p>
          </div>

          <div className="flex-1 flex items-center justify-center">
            <ResponsiveContainer width="100%" height={200}>
              <RadarChart data={radarData} margin={{ top: 8, right: 20, bottom: 8, left: 20 }}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey="criterion" tick={{ fontSize: 12, fontWeight: 700, fill: 'var(--muted-foreground)' }} />
                <Radar
                  name="Score"
                  dataKey="score"
                  stroke="var(--primary)"
                  fill="var(--primary)"
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-2">
            {radarData.map((r) => (
              <div key={r.criterion} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                <span className="text-xs font-bold text-muted-foreground">{r.criterion}</span>
                <span className={`text-sm font-extrabold band-score-display ${getBandTextClass(r.score)}`}>{r.score.toFixed(1)}</span>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-muted/40 border">
            <TrendIcon size={16} className={overallTrend.color} />
            <p className="text-xs text-muted-foreground">
              Overall band <span className={`font-bold ${overallTrend.color}`}>{overallTrend.label}</span> vs previous essay
            </p>
          </div>
        </div>
      </div>

      {/* Criterion Performance Bar Chart */}
      <div className="bg-card rounded-2xl border shadow-sm p-5 mb-4">
        <div className="mb-4">
          <p className="text-sm font-semibold text-foreground">Performance by Criterion</p>
          <p className="text-xs text-muted-foreground mt-0.5">Average vs latest essay scores across all four IELTS criteria</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={criterionBarData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="code" tick={{ fontSize: 11, fontWeight: 700, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <YAxis domain={[4, 9]} ticks={[4, 5, 6, 7, 8, 9]} tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} axisLine={false} tickLine={false} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="bg-card border rounded-xl shadow-lg px-4 py-3 text-xs">
                      <p className="font-semibold text-foreground mb-2">{label}</p>
                      {payload.map((p: { name: string; value: number; color: string }) => (
                        <div key={p.name} className="flex items-center justify-between gap-4 mb-1">
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                            <span className="text-muted-foreground">{p.name}</span>
                          </span>
                          <span className="font-bold text-foreground">{Number(p.value).toFixed(1)}</span>
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              <Bar dataKey="avg" name="Average" radius={[4, 4, 0, 0]} fill="var(--secondary)">
                {criterionBarData.map((entry) => (
                  <Cell key={`avg-${entry.code}`} fill={getBandColor(entry.avg)} opacity={0.35} />
                ))}
              </Bar>
              <Bar dataKey="latest" name="Latest" radius={[4, 4, 0, 0]}>
                {criterionBarData.map((entry) => (
                  <Cell key={`latest-${entry.code}`} fill={getBandColor(entry.latest)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="space-y-3">
            {criterionBarData.map((c) => {
              const diff = parseFloat((c.latest - c.avg).toFixed(1));
              const isUp = diff > 0;
              const isDown = diff < 0;
              return (
                <div key={c.code} className={`rounded-xl border p-4 ${getBandBgClass(c.latest)}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded">{c.code}</span>
                      <span className="text-sm font-semibold text-foreground">{c.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">avg {c.avg.toFixed(1)}</span>
                      <span className={`band-score-display text-xl font-extrabold ${getBandTextClass(c.latest)}`}>{c.latest.toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isUp && <TrendingUp size={12} className="text-[color:var(--success)]" />}
                    {isDown && <TrendingDown size={12} className="text-danger" />}
                    {!isUp && !isDown && <Minus size={12} className="text-muted-foreground" />}
                    <span className={`text-xs font-semibold ${isUp ? 'text-[color:var(--success)]' : isDown ? 'text-danger' : 'text-muted-foreground'}`}>
                      {isUp ? `+${diff}` : isDown ? `${diff}` : 'No change'} vs average
                    </span>
                    <span className="text-xs text-muted-foreground ml-1">· {getBandLabel(c.latest)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Two-column: Essay History + Recommended Topics */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

        {/* Essay History */}
        <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b bg-muted/20 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Essay History</p>
              <p className="text-xs text-muted-foreground mt-0.5">{totalEssays} essays submitted</p>
            </div>
            <Calendar size={16} className="text-muted-foreground" />
          </div>
          <div className="divide-y">
            {[...essayHistory].reverse().map((essay) => (
              <div key={essay.id} className="px-5 py-3.5 hover:bg-muted/30 transition-all duration-150 group">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="info">{essay.type}</Badge>
                      <Badge variant="secondary">{essay.topic}</Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Clock size={11} />{essay.date}</span>
                      <span className="flex items-center gap-1"><Hash size={11} />{essay.wordCount} words</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end shrink-0">
                    <span className={`band-score-display text-2xl font-extrabold ${getBandTextClass(essay.overallBand)}`}>
                      {essay.overallBand.toFixed(1)}
                    </span>
                    <span className="text-xs text-muted-foreground">{getBandLabel(essay.overallBand)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {(['TR', 'CC', 'LR', 'GRA'] as const).map((key) => (
                    <div key={key} className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground font-medium">{key}</span>
                      <span className={`text-xs font-bold ${getBandTextClass(essay[key])}`}>{essay[key].toFixed(1)}</span>
                    </div>
                  ))}
                  <Link
                    href="/assessment-results"
                    className="ml-auto flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-all duration-150"
                  >
                    View <ArrowUpRight size={11} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommended Practice Topics */}
        <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b bg-muted/20 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">Recommended Practice Topics</p>
              <p className="text-xs text-muted-foreground mt-0.5">Personalised based on your weak areas</p>
            </div>
            <Lightbulb size={16} className="text-[color:var(--warning)]" />
          </div>
          <div className="divide-y">
            {recommendedTopics.map((rec) => (
              <div key={rec.id} className="px-5 py-4 hover:bg-muted/30 transition-all duration-150">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={priorityVariant[rec.priority]}>{rec.priority} Priority</Badge>
                    <span className="text-xs font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded">{rec.criterion}</span>
                    <span className="text-xs text-muted-foreground">{rec.essayType}</span>
                  </div>
                </div>
                <p className="text-sm font-semibold text-foreground mb-1.5">{rec.topic}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{rec.reason}</p>
                <Link
                  href="/"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-all duration-150"
                >
                  <BookOpen size={12} />
                  Practise this topic
                  <ChevronRight size={11} />
                </Link>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
